import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import { AccountHealthStatus } from '../../api/client'

type Props = {
    /** The pill's word: "auth", "sub", "jobs". */
    label: string
    status: AccountHealthStatus
    /** The tooltip: what is wrong, or what is going right. */
    detail?: string | null
}

/**
 * One account health signal, inline beside its siblings on the profile row.
 *
 * Matches the auth/sub pills it sits next to — same 11px, same icon size —
 * but reads its state from a signal rather than running its own query, so a
 * page of profiles costs one request instead of one per pill. Every health
 * datum renders through this: a new signal is a query, not a design.
 */
export default function HealthPill({ label, status, detail }: Props) {
    const ok = status === AccountHealthStatus.Ok
    const warning = status === AccountHealthStatus.Warning
    const Icon = ok ? CheckCircleIcon : warning ? ExclamationCircleIcon : XCircleIcon
    return (
        <span className="inline-flex items-center gap-1 text-[11px]" title={detail ?? undefined}>
            <Icon
                className={`h-4 w-4 ${
                    ok ? 'text-safe-500' : warning ? 'text-suspect-500' : 'text-severe-500'
                }`}
                aria-hidden="true"
            />
            <span
                className={ok ? 'text-ink-400' : warning ? 'text-suspect-700' : 'text-severe-600'}
            >
                {label}
            </span>
        </span>
    )
}
